import { useTranslation } from "@/i18n";
import StepCard from "./components/StepCard";

/**
 * Node structure from the process model.
 */
interface ProcessNode {
  readonly id?: string;
  readonly name?: string;
  readonly label?: string;
  readonly type?: string;
  readonly description?: string;
  readonly text?: string;
}

/**
 * Edge structure from the process model.
 */
interface ProcessEdge {
  readonly from?: string;
  readonly source?: string;
  readonly start?: string;
  readonly sourceId?: string;
  readonly to?: string;
  readonly target?: string;
  readonly end?: string;
  readonly targetId?: string;
}

/**
 * Props for the ProcessSteps component.
 */
interface ProcessStepsProps {
  /** Process model containing nodes and edges */
  readonly model: {
    nodes?: ProcessNode[];
    edges?: ProcessEdge[];
  };
}

/**
 * ProcessSteps component displays the process flow as a series of step cards.
 * Automatically orders steps by following edges from START node.
 *
 * @param props - Component props
 * @returns Process steps visualization UI
 */
export default function ProcessSteps({ model }: ProcessStepsProps) {
  const { t } = useTranslation();
  const nodes = Array.isArray(model?.nodes) ? model.nodes : [];
  const edges = Array.isArray(model?.edges) ? model.edges : [];

  // Build next-step mapping
  const nextMap = new Map<string, string[]>();
  edges.forEach((e) => {
    const from = e?.from || e?.source || e?.start || e?.sourceId;
    const to = e?.to || e?.target || e?.end || e?.targetId;
    if (!from || !to) return;
    if (!nextMap.has(from)) nextMap.set(from, []);
    nextMap.get(from)!.push(to);
  });

  // Index nodes by id
  const byId = new Map<string, ProcessNode>();
  nodes.forEach((n) => {
    if (n?.id) byId.set(n.id, n);
  });

  // Display order: try to follow edges from a START if present
  const startNode = nodes.find(
    (n) => (n?.type || "").toUpperCase() === "START"
  );
  const ordered: ProcessNode[] = [];
  const visited = new Set<string>();

  const pushNode = (id: string) => {
    if (!id || visited.has(id)) return;
    const n = byId.get(id);
    if (!n) return;
    visited.add(id);
    ordered.push(n);
    const toList = nextMap.get(id) || [];
    toList.forEach(pushNode);
  };

  if (startNode?.id) pushNode(startNode.id);
  nodes.forEach((n) => {
    if (n?.id && !visited.has(n.id)) pushNode(n.id);
  });

  return (
    <div className="space-y-5">
      <h3 className="text-base font-semibold">
        {t("documentReview.processSteps.title")}
      </h3>

      <div className="space-y-4">
        {ordered.map((node, index) => {
          const title =
            node?.name ||
            node?.label ||
            t("documentReview.processSteps.stepLabel", { number: index + 1 });
          const nodeType = node?.type?.toUpperCase() || "TASK";
          const description = node?.description || node?.text || "";
          const nexts = (nextMap.get(node?.id || "") || [])
            .map((id) => byId.get(id)?.label || id)
            .filter(Boolean);

          // Find the original index in the nodes array
          const originalIndex = nodes.findIndex((n) => n?.id === node?.id);

          return (
            <StepCard
              key={node?.id || index}
              title={title}
              nodeType={nodeType}
              description={description}
              nextSteps={nexts}
              stepIndex={originalIndex >= 0 ? originalIndex : index}
            />
          );
        })}
      </div>
    </div>
  );
}
